import _ from 'lodash';
import {
  getInputAndIntermediateIds,
  toTemplateInputsFromTable,
  toTemplateIntermediatesFromTable,
} from '@/tableDefinitionEditor/templateColumn.utilities';
import { setActiveTool } from '@/toolSelection/investigate.actions';
import { TREND_TOOLS } from '@/toolSelection/investigate.constants';
import { captureInputRestrictionType } from '@/tools/models/outputItemMetadata.utilities';
import { resetTemplate, setTemplate } from '@/tools/models/template.actions';
import { saveDraft } from '@/tools/models/template.api';
import { TEMPLATE_TYPES } from '@/tools/models/template.types';
import {
  featureNameToIdentifier,
  getDerivedFeatureId,
  isFeatureTypeDerivedFeatureWithColumnDefinition,
  makeUniqueIdentifier,
} from '@/tools/models/template.utilities';
import { formulaReferencesVariable, renameFormulaVariables } from '@/utilities/formulaVariables.utilities';
import { successToast } from '@/utilities/toast.utilities';
import { getNextDefaultName } from '@/utilities/utilities';
import { fetchTemplates } from '@/workbook/workbook.actions';
import { sqTemplatesApi } from '@seeqdev/api/endpoint/TemplatesApi';
import { OutputTypeEnum } from '@seeqdev/api/model/InputFeatureV1';
import { TypeEnum as TemplateRuleInputV1ListStringTypeEnum } from '@seeqdev/api/model/TemplateRuleInputV1ListString';
import { TypeEnum as TemplateRuleInputV1ObjectTypeEnum } from '@seeqdev/api/model/TemplateRuleInputV1Object';
import { TypeEnum as TemplateRuleInputV1StringTypeEnum } from '@seeqdev/api/model/TemplateRuleInputV1String';
import { TypeEnum as TemplateRuleInputV1UUIDTypeEnum } from '@seeqdev/api/model/TemplateRuleInputV1UUID';
import type { FormulaEditorParam } from '@/formula/FormulaParametersTable.molecule';
import type { ColumnDefinition } from '@/tableDefinitionEditor/tableDefinition.types';
import type { FormulaItemTableOption } from '@/tools/formula/FormulaItemTable.molecule';
import type {
  FormulaOutput,
  NewlyAddedDerivedFeature,
  NewlyAddedInputFeature,
  OutputItemMetadataFields,
  TemplateDerivedFeature,
  TemplateInput,
  TemplateInputIds,
  TemplateOutput,
  TemplateRuleInputWithFeatureId,
  TemplateType,
} from '@/tools/models/template.types';
import type { ColumnRuleOutputV1 } from '@seeqdev/api/model/ColumnRuleOutputV1';
import type { DerivedFeatureV1 } from '@seeqdev/api/model/DerivedFeatureV1';
import type { FeatureRuleV1 } from '@seeqdev/api/model/FeatureRuleV1';
import type { InputFeatureV1 } from '@seeqdev/api/model/InputFeatureV1';
import type { TemplateFormulaCreatorRuleV1 } from '@seeqdev/api/model/TemplateFormulaCreatorRuleV1';
import type { TemplateInputV1 } from '@seeqdev/api/model/TemplateInputV1';
import type { TemplateRuleInputV1ListString } from '@seeqdev/api/model/TemplateRuleInputV1ListString';
import type { TemplateRuleInputV1Object } from '@seeqdev/api/model/TemplateRuleInputV1Object';
import type { TemplateRuleInputV1String } from '@seeqdev/api/model/TemplateRuleInputV1String';
import type { TemplateRuleInputV1UUID } from '@seeqdev/api/model/TemplateRuleInputV1UUID';

/** Given a set of output columns, recursively finds all input columns and intermediate columns (those that are neither
 * inputs nor outputs) and returns updated lists of inputs and outputs.
 * @param outputColumns The new output columns to generate inputs and intermediates from
 * @param inputs The existing list of inputs to avoid duplicates
 * @param existingOutputs The existing list of outputs that should be preserved
 * @param tableColumns The full list of columns in the table
 */
export const getUpdatedInputsAndOutputs = (
  outputColumns: ColumnDefinition[] | DerivedFeatureV1[],
  inputs: TemplateInput[] | InputFeatureV1[],
  existingOutputs: TemplateOutput[],
  tableColumns: ColumnDefinition[],
) => {
  if (!outputColumns?.length && !existingOutputs?.length) {
    if (inputs.length) {
      setTemplate(TEMPLATE_TYPES.SCALING, { inputFeatures: [], derivedFeatures: [] });
    }
    return;
  }

  const columnIdMap = new Map<string, ColumnDefinition>(tableColumns.map((c) => [c.id, c]));
  const outputIds = outputColumns.map((c) => c.id).filter((id): id is string => !!id);

  const inputIds: string[] = [];
  const intermediateIds: string[] = [];
  outputColumns.forEach((col) => {
    getInputAndIntermediateIds(col.id as string, [], inputIds, intermediateIds, outputIds, columnIdMap, false);
  });

  // TODO CRAB-61003: Remove cast when edit flow is migrated to NewlyAddedInputFeature in CRAB-XXXXX
  const newInputs = [
    ...(inputs as NewlyAddedInputFeature[]),
    ...toTemplateInputsFromTable(inputIds, inputs as NewlyAddedInputFeature[], columnIdMap),
  ];
  const foundInputs: string[] = [];
  const uniqueInputs: NewlyAddedInputFeature[] = newInputs.filter((i) => {
    const ref = i.referenceId?.toLowerCase();
    if (!ref) return true;
    if (foundInputs.includes(ref)) return false;
    foundInputs.push(ref);
    return true;
  });

  const newIntermediateNames = toTemplateIntermediatesFromTable(intermediateIds, columnIdMap, [], []).map(
    (intermediate) => intermediate.name,
  );
  const matchingIntermediateColumns = _.chain(tableColumns)
    .filter((col) => newIntermediateNames.includes(col.displayName))
    .map((col) => ({ ...col, isOutput: false }))
    .uniqBy('id')
    .value();

  const newOutputs = _.uniqBy(
    [...outputColumns, ...existingOutputs, ...matchingIntermediateColumns],
    (f) => f.id ?? '',
  );

  return { inputs: uniqueInputs, outputs: newOutputs };
};

/** Determines if a feature is used by any output derived features.
 *
 * @param featureId - The ID of the feature to check
 * @param derivedFeatures - The current list of derived features in the template
 * @param checkingInputs - True to check only inputs, false to check both outputs and intermediates
 * @returns True if the intermediate is used by any outputs, false otherwise
 */
export const isFeatureUsedByOtherOutputs = (
  featureId: string,
  derivedFeatures: DerivedFeatureV1[],
  checkingInputs = false,
) => {
  const isStringArray = (a: unknown[]): boolean => a.every((v) => typeof v === 'string');

  return derivedFeatures.some((feature) => {
    if (feature.id === featureId || (feature.isOutput === false && !checkingInputs)) {
      return false;
    }
    return feature.rules?.some((rule) => {
      const inputs = rule.inputs as unknown;

      if (Array.isArray(inputs)) {
        if (rule.inputs.some((input) => typeof input === 'object')) {
          return inputs.some((i) => i.inputFeatureId === featureId);
        }
        if (isStringArray(inputs)) {
          return inputs.includes(featureId);
        }
        return false;
      }
      return false;
    });
  });
};

/** Converts an intermediate to an output if convertToIntermediate is false, otherwise converts an output to an
 *  intermediate
 *
 *  @param featureIdToConvert - The ID of the derived feature to convert
 *  @param derivedFeatures - The current list of derived features in the template
 *  @param convertToIntermediate - True to convert to intermediate, false to convert to output
 *  @param templateType - The type of template being edited, used to determine which store to update
 *  */
export const convertDerivedFeature = (
  featureIdToConvert: string,
  derivedFeatures: DerivedFeatureV1[],
  convertToIntermediate = false,
  templateType: TemplateType = TEMPLATE_TYPES.SCALING,
) => {
  const getDerivedFeatureId = (feature: DerivedFeatureV1) =>
    'featureId' in feature && feature.featureId ? feature.featureId : feature.id;

  const updatedDerivedFeatures = derivedFeatures.map((feature) =>
    getDerivedFeatureId(feature) === featureIdToConvert ? { ...feature, isOutput: !convertToIntermediate } : feature,
  ) as unknown as TemplateOutput[];

  setTemplate(templateType, { derivedFeatures: updatedDerivedFeatures });
};

/**
 * Edits a template by updating all of its template details.
 */
// TODO(CRAB-58652): Extract to template.api.ts — network call, not a pure utility.
export const editTemplate = async (templateId: string, input: TemplateInputV1) => {
  const { data: templateOutput } = await sqTemplatesApi.editTemplate(input, { templateId });
  return templateOutput;
};

const getInputIndex = (inputId: string, inputFeatures: TemplateInput[], inputFeatureId?: string) => {
  if (!inputFeatures?.length) return undefined;
  if (inputFeatureId) {
    return inputFeatures.findIndex(
      (feature) => feature?.id === inputFeatureId || feature?.columnDefinition?.id === inputFeatureId,
    );
  }
  return inputFeatures.findIndex(
    (feature) =>
      feature?.referenceId === inputId || feature?.id === inputId || feature?.columnDefinition?.id === inputId,
  );
};

const getDerivedIndex = (id: string, derivedFeatures: TemplateOutput[], inputFeatureId?: string) => {
  if (!derivedFeatures?.length) return undefined;
  if (inputFeatureId) {
    return derivedFeatures.findIndex(
      (feature) => feature?.id === inputFeatureId || ('featureId' in feature && feature?.featureId === inputFeatureId),
    );
  }
  return derivedFeatures.findIndex(
    (feature) =>
      feature?.id === id ||
      ('referenceId' in feature && feature?.referenceId === id) ||
      ('featureId' in feature && feature.featureId === id) ||
      (isFeatureTypeDerivedFeatureWithColumnDefinition(feature) && feature.columnDefinition?.id === id),
  );
};

const getDerivedFeatureNameForRuleSave = (feature: TemplateOutput): string => {
  if ('displayName' in feature && feature.displayName) {
    return String(feature.displayName);
  }
  return feature.name ?? '';
};

/** True when an input feature already has a persisted template id that should be referenced by inputFeatureId. */
const shouldUseInputFeatureIdForRule = (feature: TemplateInput | undefined): boolean => {
  if (!feature?.id) {
    return false;
  }
  // New tool-pane inputs carry the Seeq item id in both id and referenceId; the backend assigns
  // template ids on create, so rules must reference these inputs by index instead.
  if ('referenceId' in feature && feature.referenceId && feature.id === feature.referenceId) {
    return !!(feature.canonicalId || feature.columnDefinition?.id);
  }
  return true;
};

const splitColumnRuleParameters = (parameters: string | undefined): string[] => {
  if (!parameters) {
    return [];
  }
  return parameters.includes('|') ? parameters.split('|') : parameters.split(',');
};

const getExistingFormulaCreatorParameterNames = (formulaCreator: TemplateFormulaCreatorRuleV1): string[] => {
  const parameters = formulaCreator.parameters?.argument;
  return Array.isArray(parameters) ? parameters : [];
};

const getEditedFormulaCreatorParameterNames = (editedRule: ColumnRuleOutputV1): string[] =>
  splitColumnRuleParameters(editedRule.arguments?.parameters);

/** Resolves the FormulaCreator name from preview `ColumnRuleOutputV1` rules (static or column-bound). */
const getEditedFormulaCreatorNameArgument = (
  editedRule: ColumnRuleOutputV1 | undefined,
  tableColumns: ColumnDefinition[],
  fallbackName: string | undefined,
): string | undefined => {
  if (!editedRule) {
    return fallbackName;
  }

  const boundNameColumnId = editedRule.boundArguments?.name;
  if (boundNameColumnId) {
    const boundColumn = tableColumns.find((column) => column.id === boundNameColumnId);
    return boundColumn?.displayName ?? boundColumn?.columnName ?? editedRule.arguments?.name ?? fallbackName;
  }

  return editedRule.arguments?.name ?? fallbackName;
};

const haveSameParameterNames = (existing: string[], edited: string[]): boolean =>
  existing.length === edited.length && existing.every((name, index) => name === edited[index]);

const getColumnReferenceFromModelFeature = (
  feature: TemplateInput | TemplateOutput | undefined,
): string | undefined => {
  if (!feature) {
    return undefined;
  }
  const columnDefinitionId = 'columnDefinition' in feature ? feature.columnDefinition?.id : undefined;
  if (columnDefinitionId) {
    return columnDefinitionId;
  }
  if ('referenceId' in feature && feature.referenceId) {
    return feature.referenceId;
  }
  return undefined;
};

const findInputFeatureByReference = (referenceId: string, inputFeatures: TemplateInput[]): TemplateInput | undefined =>
  inputFeatures.find(
    (feature) =>
      feature.id === referenceId || feature.referenceId === referenceId || feature.columnDefinition?.id === referenceId,
  );

const findDerivedFeatureByReference = (
  referenceId: string,
  derivedFeatures: TemplateOutput[],
): TemplateOutput | undefined =>
  derivedFeatures.find(
    (feature) =>
      feature.id === referenceId ||
      ('referenceId' in feature && feature.referenceId === referenceId) ||
      ('featureId' in feature && feature.featureId === referenceId) ||
      (isFeatureTypeDerivedFeatureWithColumnDefinition(feature) && feature.columnDefinition.id === referenceId),
  );

const resolveExistingRuleInputReference = (
  input: TemplateRuleInputV1Object,
  inputFeatures: TemplateInput[],
  derivedFeatures: TemplateOutput[],
  tableColumns: ColumnDefinition[],
): string | undefined => {
  const isLiteralInput =
    input.argument !== undefined &&
    input.inputFeatureId === undefined &&
    input.inputFeatureIndex === undefined &&
    input.derivedFeatureIndex === undefined &&
    !hasFeatureId(input);
  if (isLiteralInput) {
    return `literal:${String(input.argument)}`;
  }

  if (input.inputFeatureIndex != null) {
    const inputFeature = inputFeatures[input.inputFeatureIndex];
    return getColumnReferenceFromModelFeature(inputFeature) ?? inputFeature?.id;
  }

  if (input.derivedFeatureIndex != null) {
    const derivedFeature = derivedFeatures[input.derivedFeatureIndex];
    const columnReference = getColumnReferenceFromModelFeature(derivedFeature);
    if (columnReference) {
      return columnReference;
    }
    if (derivedFeature && 'featureId' in derivedFeature) {
      return derivedFeature.featureId as string;
    }
    return derivedFeature?.id;
  }

  const inputFeatureId = input.inputFeatureId ?? getFeatureIdFromInput(input);
  if (inputFeatureId) {
    const matchingInput = findInputFeatureByReference(inputFeatureId, inputFeatures);
    if (matchingInput) {
      return getColumnReferenceFromModelFeature(matchingInput) ?? matchingInput.id;
    }

    const matchingDerived = findDerivedFeatureByReference(inputFeatureId, derivedFeatures);
    if (matchingDerived) {
      const columnReference = getColumnReferenceFromModelFeature(matchingDerived);
      if (columnReference) {
        return columnReference;
      }
      if ('featureId' in matchingDerived) {
        return matchingDerived.featureId as string;
      }
      return matchingDerived.id;
    }

    if (tableColumns.some((column) => column.id === inputFeatureId)) {
      return inputFeatureId;
    }

    return inputFeatureId;
  }

  return undefined;
};

const resolveExistingRuleInputReferences = (
  existingRule: FeatureRuleV1,
  inputFeatures: TemplateInput[],
  derivedFeatures: TemplateOutput[],
  tableColumns: ColumnDefinition[],
): (string | undefined)[] =>
  (existingRule.inputs ?? []).map((input) =>
    resolveExistingRuleInputReference(input as TemplateRuleInputV1Object, inputFeatures, derivedFeatures, tableColumns),
  );

const resolveEditedRuleInputReferences = (editedRule: ColumnRuleOutputV1): string[] =>
  (editedRule.inputs ?? []).map((inputId) => String(inputId));

const haveSameResolvedInputReferences = (
  existingReferences: (string | undefined)[],
  editedReferences: string[],
): boolean =>
  existingReferences.length === editedReferences.length &&
  existingReferences.every((reference, index) => reference !== undefined && reference === editedReferences[index]);

const isRuleWithFormulaCreator = (
  rule: ColumnRuleOutputV1,
): rule is ColumnRuleOutputV1 & { formulaCreator: TemplateFormulaCreatorRuleV1 } =>
  'formulaCreator' in rule &&
  !!rule.formulaCreator &&
  typeof rule.formulaCreator === 'object' &&
  'formula' in rule.formulaCreator &&
  'name' in rule.formulaCreator &&
  'parameters' in rule.formulaCreator;

const normalizeFormulaCreatorNameForSave = (
  formulaCreator: TemplateFormulaCreatorRuleV1,
  featureName: string,
): TemplateFormulaCreatorRuleV1 => {
  const nameInput = formulaCreator.name;
  const hasValidNameReference =
    nameInput?.argument != null ||
    nameInput?.inputFeatureId != null ||
    nameInput?.inputFeatureIndex != null ||
    nameInput?.derivedFeatureIndex != null;

  if (hasValidNameReference) {
    return formulaCreator;
  }

  return {
    ...formulaCreator,
    name: {
      name: 'name',
      description: 'name argument for FormulaCreator',
      type: OutputTypeEnum.TEXT,
      argument: featureName,
    },
  };
};

/** Type guard to check if an input has the featureId field. */
function hasFeatureId(input: TemplateRuleInputV1Object): input is TemplateRuleInputWithFeatureId {
  return 'featureId' in input;
}

/** Gets the ID of a feature from a rule input. Prefer inputFeatureId (template feature ID) over featureId (Seeq item
 *  ID). */
const getFeatureIdFromInput = (input: TemplateRuleInputV1Object): string | undefined =>
  input.inputFeatureId ?? (hasFeatureId(input) ? input.featureId : undefined);

/** Enriches all rule inputs with featureId tracking field by resolving current array indices.
 * Used internally by filterUnusedFeatures (for dependency tracking) and reindexNewDerivedFeatures
 * (for reliable index lookups). Preserves object identity when no enrichment is needed. */
function enrichNewDerivedFeaturesWithFeatureId(
  newDerivedFeatures: NewlyAddedDerivedFeature[],
  newInputFeatures: NewlyAddedInputFeature[],
): NewlyAddedDerivedFeature[] {
  return newDerivedFeatures.map((feature) => {
    if (!feature.rules?.length) return feature;

    // Track if anything changed so we only rebuild objects when necessary.
    // Preserves identity after initial enrichment to avoid wasted allocations.
    let anyRuleChanged = false;
    const enrichedRules = feature.rules.map((rule) => {
      if (!rule.inputs) return rule;

      let anyInputChanged = false;
      const enrichedInputs = rule.inputs.map((input) => {
        if (getFeatureIdFromInput(input) != null) return input;

        // Resolve featureId from current indices
        let featureId: string | undefined;
        if (input.derivedFeatureIndex !== undefined && input.derivedFeatureIndex >= 0) {
          const ref = newDerivedFeatures[input.derivedFeatureIndex];
          featureId = getDerivedFeatureId(ref);
        } else if (input.inputFeatureIndex !== undefined && input.inputFeatureIndex >= 0) {
          const ref = newInputFeatures[input.inputFeatureIndex];
          featureId = ref?.referenceId;
        }

        if (featureId) {
          anyInputChanged = true;
          return { ...input, featureId };
        }
        return input;
      });

      if (anyInputChanged) {
        anyRuleChanged = true;
        return { ...rule, inputs: enrichedInputs };
      }
      return rule;
    });

    return anyRuleChanged ? { ...feature, rules: enrichedRules } : feature;
  });
}

const hasValidTemplateRuleInputReference = (input: TemplateRuleInputV1Object): boolean =>
  input.argument != null ||
  input.inputFeatureId != null ||
  input.inputFeatureIndex != null ||
  input.derivedFeatureIndex != null;

const getTemplateFeatureName = (feature: TemplateOutput | TemplateInput | undefined, fallback = ''): string => {
  if (!feature) {
    return fallback;
  }
  if ('displayName' in feature && feature.displayName) {
    return feature.displayName;
  }
  return feature.name ?? fallback;
};

const mapRuleInputToFeature = (
  inputId: string | TemplateRuleInputV1Object,
  inputFeatures: TemplateInput[],
  derivedFeatures: TemplateOutput[],
  featureType: TemplateRuleInputV1Object['type'],
): TemplateRuleInputV1Object => {
  if (typeof inputId === 'object') {
    const persistedInput = inputId;

    if (
      persistedInput.argument !== undefined &&
      persistedInput.inputFeatureId === undefined &&
      persistedInput.inputFeatureIndex === undefined &&
      persistedInput.derivedFeatureIndex === undefined &&
      !hasFeatureId(persistedInput)
    ) {
      return persistedInput;
    }
  }

  const lookupId = typeof inputId === 'object' ? (inputId.inputFeatureId ?? getFeatureIdFromInput(inputId)) : undefined;
  const inputIndexUnmapped = getInputIndex(typeof inputId === 'string' ? inputId : '', inputFeatures, lookupId);
  let finalInputIndex = inputIndexUnmapped;
  const derivedIndex = getDerivedIndex(typeof inputId === 'string' ? inputId : '', derivedFeatures, lookupId);
  let matchingInputFeature: TemplateInput | undefined;

  if (inputIndexUnmapped !== undefined && inputIndexUnmapped >= 0) {
    const feature = inputFeatures[inputIndexUnmapped];
    if (feature.mappedFeatureId) {
      const mappedIndex = inputFeatures.findIndex((f) => f.id === feature.mappedFeatureId);
      if (mappedIndex >= 0) {
        matchingInputFeature = inputFeatures[mappedIndex];
        finalInputIndex = mappedIndex;
      } else {
        matchingInputFeature = feature;
      }
    } else {
      matchingInputFeature = feature;
    }
  } else {
    matchingInputFeature = undefined;
  }

  const matchingDerivedFeature =
    derivedIndex !== undefined && derivedIndex >= 0 ? derivedFeatures[derivedIndex] : undefined;
  const feature = matchingInputFeature ?? matchingDerivedFeature;
  const featureId =
    matchingInputFeature?.referenceId ?? matchingDerivedFeature?.referenceId ?? matchingDerivedFeature?.id;
  const persistedName = typeof inputId === 'object' ? inputId.name : undefined;
  const persistedDescription = typeof inputId === 'object' ? inputId.description : undefined;

  if (finalInputIndex !== undefined && finalInputIndex >= 0) {
    if (shouldUseInputFeatureIdForRule(matchingInputFeature)) {
      return {
        name: getTemplateFeatureName(feature, persistedName ?? ''),
        description: feature?.description ?? persistedDescription ?? '',
        type: featureType,
        inputFeatureId: matchingInputFeature!.id,
      };
    }
    return {
      name: getTemplateFeatureName(feature, persistedName ?? ''),
      description: feature?.description ?? persistedDescription ?? '',
      type: featureType,
      inputFeatureIndex: finalInputIndex,
    };
  }

  if (derivedIndex !== undefined && derivedIndex >= 0) {
    return {
      name: getTemplateFeatureName(feature, persistedName ?? ''),
      description: feature?.description ?? persistedDescription ?? '',
      type: featureType,
      derivedFeatureIndex: derivedIndex,
    };
  }

  if (matchingInputFeature?.id && !matchingInputFeature.referenceId) {
    return {
      name: matchingInputFeature.name ?? persistedName ?? '',
      description: matchingInputFeature.description ?? persistedDescription ?? '',
      type: featureType,
      inputFeatureId: matchingInputFeature.id,
    };
  }

  if (lookupId || persistedName) {
    const matchingInput = inputFeatures.find(
      (input) =>
        (lookupId &&
          (input.id === lookupId || input.referenceId === lookupId || input.columnDefinition?.id === lookupId)) ||
        (persistedName && input.name === persistedName),
    );
    if (matchingInput) {
      if (shouldUseInputFeatureIdForRule(matchingInput)) {
        return {
          name: matchingInput.name ?? persistedName ?? '',
          description: matchingInput.description ?? persistedDescription ?? '',
          type: featureType,
          inputFeatureId: matchingInput.id,
        };
      }
      const inputIndex = inputFeatures.indexOf(matchingInput);
      if (inputIndex >= 0) {
        return {
          name: matchingInput.name ?? persistedName ?? '',
          description: matchingInput.description ?? persistedDescription ?? '',
          type: featureType,
          inputFeatureIndex: inputIndex,
        };
      }
    }

    if (typeof inputId === 'object' && inputId.inputFeatureId && inputId.inputFeatureId === lookupId) {
      return {
        name: persistedName ?? '',
        description: persistedDescription ?? '',
        type: featureType,
        inputFeatureId: lookupId,
      };
    }
  }

  const fallbackInput: TemplateRuleInputV1Object = {
    name: getTemplateFeatureName(feature, persistedName ?? ''),
    description: feature?.description ?? persistedDescription ?? '',
    type: featureType,
    ...(shouldUseInputFeatureIdForRule(matchingInputFeature) && featureId && { inputFeatureId: featureId }),
  };

  if (!hasValidTemplateRuleInputReference(fallbackInput)) {
    throw new Error(`Unable to resolve template rule input reference for save: ${JSON.stringify(inputId)}`);
  }

  return fallbackInput;
};

/** Maps rule inputs to the expected feature format for the backend. If an input feature has a mappedFeatureId, it
 *  uses that feature instead.
 *
 * @param ruleInputs - The rule inputs to map
 * @param inputFeatures - The current list of input features in the template
 * @param derivedFeatures - The current list of derived features in the template
 * @param featureType - The feature type to use for the mapped features
 * @returns An array of mapped features
 */
export function mapRuleInputsToFeatures(
  ruleInputs: Array<string | TemplateRuleInputV1Object>,
  inputFeatures: TemplateInput[],
  derivedFeatures: TemplateOutput[],
  featureType: TemplateRuleInputV1Object['type'],
) {
  return (ruleInputs ?? []).map((inputId) =>
    mapRuleInputToFeature(inputId, inputFeatures, derivedFeatures, featureType),
  );
}

/** Converts rules for a newly created derived feature into the format expected by the backend
 *
 * @param rules - The rules to convert
 * @param inputFeatures - The current list of input features in the template
 * @param derivedFeatures - The current list of derived features in the template
 * @param newFeature - The newly created derived feature
 * @param scopedTo - The scopedTo value for the template
 * */
export const convertNewRules = (
  rules: ColumnRuleOutputV1[],
  inputFeatures: TemplateInput[],
  derivedFeatures: TemplateOutput[],
  newFeature: TemplateOutput,
  scopedTo: string | undefined,
) => {
  const featureName = getDerivedFeatureNameForRuleSave(newFeature);

  return rules.map((rule) => {
    const ruleInputs = rule.inputs ?? [];
    const mappedInputs = mapRuleInputsToFeatures(
      ruleInputs as Array<string | TemplateRuleInputV1Object>,
      inputFeatures,
      derivedFeatures,
      TemplateRuleInputV1ObjectTypeEnum.FEATUREID,
    );

    if (isRuleWithFormulaCreator(rule)) {
      return {
        inputs: mappedInputs,
        formulaCreator: normalizeFormulaCreatorNameForSave(rule.formulaCreator, featureName),
      };
    }

    // Prefer the rule's own scopedTo (preserved through the preview API as `rule.arguments.scopedTo`)
    // over the template-level scopedTo. This matters when an existing template feature is re-saved
    // after losing its rich `formulaCreator` template metadata via the preview round-trip
    const effectiveScopedTo = rule.arguments.scopedTo ?? scopedTo;
    return {
      inputs: mappedInputs,
      formulaCreator: {
        formula: {
          name: 'formula',
          description: 'formula argument for FormulaCreator',
          type: OutputTypeEnum.TEXT,
          argument: rule.arguments.formula,
        },
        parameters: {
          name: 'parameters',
          description: 'parameters argument for FormulaCreator',
          type: OutputTypeEnum.TEXT,
          argument: splitColumnRuleParameters(rule.arguments.parameters),
        },
        name: {
          name: 'name',
          description: 'name argument for FormulaCreator',
          type: OutputTypeEnum.TEXT,
          argument: featureName,
        },
        ...(effectiveScopedTo !== undefined && {
          scopedTo: {
            name: 'scopedTo',
            description: 'scopedTo argument for FormulaCreator',
            type: OutputTypeEnum.ITEMID,
            argument: effectiveScopedTo,
          },
        }),
      },
    };
  });
};

/**
 * Derived features materialized from table column selection during edit-template.
 * These are not persisted template features and must not be sent as existing features on save.
 */
export const isTableSourcedDerivedFeature = (feature: DerivedFeatureV1): boolean => {
  if ('displayName' in feature) {
    return true;
  }
  if ('featureId' in feature) {
    return true;
  }
  // Intermediates from walking table dependencies only carry referenceId (column id), not a template feature id.
  if ('referenceId' in feature && feature.referenceId && !feature.id) {
    return true;
  }
  return false;
};

/** Rebuilds template inputs from derived feature rule references, preserving persisted input feature ids. */
export const reconcileTemplateInputFeaturesForDerivedFeatures = (
  derivedFeatures: DerivedFeatureV1[],
  columns: ColumnDefinition[],
  inputFeatures: TemplateInput[],
): TemplateInput[] =>
  updateInputs(
    derivedFeatures,
    columns,
    inputFeatures.filter((feature) => !feature?.referenceId) as TemplateInput[],
  ) as TemplateInput[];

const resolveTemplateFeatureIdForSave = (feature: DerivedFeatureV1): string | undefined => {
  if ('featureId' in feature && feature.featureId) {
    return feature.featureId as string;
  }
  return feature.id;
};

const resolveTemplateFeatureCanonicalIdForSave = (
  feature: DerivedFeatureV1,
  existingDerivedFeatures?: TemplateDerivedFeature[],
): string | undefined => {
  if (feature.canonicalId) {
    return feature.canonicalId;
  }
  const templateFeatureId = resolveTemplateFeatureIdForSave(feature);
  if (!templateFeatureId || !existingDerivedFeatures?.length) {
    return undefined;
  }
  return existingDerivedFeatures.find((existingFeature) => existingFeature.id === templateFeatureId)?.canonicalId;
};

/** Formats the template for saving by converting new derived features and inputs into the expected backend format */
export function formatTemplateForSave({
  name,
  description,
  templateId,
  scopedTo,
  derivedFeatures,
  inputFeatures,
  existingDerivedFeatures,
}: {
  name: string;
  description: string;
  templateId: string;
  scopedTo?: string | undefined;
  derivedFeatures: DerivedFeatureV1[];
  inputFeatures: InputFeatureV1[];
  /** Baseline template features used to recover canonicalId when the working copy lost it (e.g. after column edit). */
  existingDerivedFeatures?: TemplateDerivedFeature[];
}): TemplateInputV1 {
  const newIntermediates = derivedFeatures.filter((feature) => feature.isOutput === false && 'displayName' in feature);
  const newDerived = derivedFeatures.filter(
    (feature) =>
      (feature.isOutput === undefined && 'displayName' in feature) ||
      (feature.isOutput === true && 'featureId' in feature),
  );
  const existingFeatures = derivedFeatures?.length
    ? derivedFeatures.filter((feature) => !isTableSourcedDerivedFeature(feature))
    : [];

  const sortInputFeatures = (features: InputFeatureV1[]) => [
    ...features.filter((feature) => !('mappedFeatureId' in feature)),
    ...features.filter((feature) => 'mappedFeatureId' in feature),
  ];

  const sortedInputFeaturesForSave = sortInputFeatures(inputFeatures);

  // Rule input indices must match the inputFeatures array sent to the backend, which includes the
  // instance name input even though formulas never reference it.
  const ruleIndexingInputFeatures = sortedInputFeaturesForSave;

  const reindexedExistingFeatures = reindexExistingDerivedFeatures(
    existingFeatures as TemplateDerivedFeature[],
    ruleIndexingInputFeatures as TemplateInput[],
  );

  // Order is important: intermediates first, then new derived outputs, then existing features, otherwise we get an
  // out-of-bounds error when converting rules that reference other derived features
  const orderedDerivedFeatures = [...newIntermediates, ...newDerived, ...reindexedExistingFeatures];

  const formattedDerivedFeatures = (orderedDerivedFeatures as DerivedFeatureV1[])
    .map((feature) => {
      if (
        ('rules' in feature && Array.isArray(feature.rules) && 'displayName' in feature) ||
        feature.isOutput !== undefined
      ) {
        const newRules = convertNewRules(
          feature.rules as unknown as ColumnRuleOutputV1[],
          ruleIndexingInputFeatures as TemplateInput[],
          orderedDerivedFeatures as unknown as TemplateOutput[],
          feature as unknown as TemplateOutput,
          scopedTo,
        );
        const templateFeatureId = resolveTemplateFeatureIdForSave(feature);
        const canonicalId = resolveTemplateFeatureCanonicalIdForSave(feature, existingDerivedFeatures);
        return {
          name: 'displayName' in feature ? feature.displayName : feature.name,
          rules: newRules,
          outputType: OutputTypeEnum.ITEMID,
          isOutput: feature?.isOutput !== undefined ? feature.isOutput : true,
          id: templateFeatureId,
          ...(canonicalId && { canonicalId }),
          ...(feature.outputItemType && { outputItemType: feature.outputItemType }),
          ...(feature.outputItemMetadata && { outputItemMetadata: feature.outputItemMetadata }),
        };
      }
      return undefined;
    })
    .filter(Boolean);

  const inputIds = (sortedInputFeaturesForSave as InputFeatureV1[]).map((feature) => feature.id).filter(Boolean);

  const formattedInputFeatures = (sortedInputFeaturesForSave as InputFeatureV1[])
    .filter((feature) => {
      if ('mappedFeatureId' in feature && feature.mappedFeatureId) {
        return !inputIds.includes(feature.mappedFeatureId as string);
      }
      return true;
    })
    .map((feature) => {
      // Resolved once per feature (rather than only inside the referenceId branch below) so the
      // fallback branch at the end can also recover a persisted restriction.
      const tableSourcedInput = feature as TemplateInput;
      const persistedMatch = tableSourcedInput.referenceId
        ? (inputFeatures as TemplateInput[]).find(
            (input) =>
              input.id &&
              (input.referenceId === tableSourcedInput.referenceId ||
                (tableSourcedInput.name && input.name === tableSourcedInput.name) ||
                (tableSourcedInput.name && input.name === tableSourcedInput.originalName)),
          )
        : undefined;
      const restrictionOutputItemType = feature.outputItemType ?? persistedMatch?.outputItemType;
      const restrictionOutputItemMetadata = feature.outputItemMetadata ?? persistedMatch?.outputItemMetadata;
      const restrictionFields = {
        ...(restrictionOutputItemType !== undefined && { outputItemType: restrictionOutputItemType }),
        ...(restrictionOutputItemMetadata !== undefined && { outputItemMetadata: restrictionOutputItemMetadata }),
      };

      if (feature.id && feature.outputType !== undefined) {
        return feature;
      }
      if ('inputFeatureId' in feature && feature.inputFeatureId) {
        return {
          description: feature.description,
          name: feature.name,
          outputType: OutputTypeEnum.ITEMID,
          id: feature.inputFeatureId,
          ...restrictionFields,
        };
      }
      if ('referenceId' in feature && feature.referenceId) {
        if (persistedMatch?.id) {
          return {
            description: feature.description ?? persistedMatch.description,
            name: feature.name ?? persistedMatch.name,
            outputType: OutputTypeEnum.ITEMID,
            id: persistedMatch.id,
            ...(persistedMatch.canonicalId && { canonicalId: persistedMatch.canonicalId }),
            ...restrictionFields,
          };
        }
      }
      return {
        description: feature.description,
        name: feature.name,
        outputType: OutputTypeEnum.ITEMID,
        ...restrictionFields,
      };
    });

  return {
    name,
    description,
    id: templateId,
    scopedTo,
    derivedFeatures: formattedDerivedFeatures,
    inputFeatures: formattedInputFeatures,
  } as unknown as TemplateInputV1;
}
export function getUpdatedIntermediates(
  intermediateIds: string[],
  derivedFeatures: DerivedFeatureV1[],
): DerivedFeatureV1[] {
  const unusedIds = intermediateIds.filter(
    (intermediateId) => !isFeatureUsedByOtherOutputs(intermediateId, derivedFeatures),
  );

  return derivedFeatures.filter((feature) => !unusedIds.includes(feature.id!));
}

/** Gets all input IDs used by a derived feature's rules
 *
 * @param removingFeature - The derived feature to get input IDs from
 * @returns An array of input IDs used by the derived feature
 */
export function getRemovingFeatureInputIds(removingFeature?: DerivedFeatureV1): string[] {
  return (
    removingFeature?.rules?.flatMap((rule) =>
      Array.isArray(rule.inputs)
        ? rule.inputs
            .map((input) =>
              typeof input === 'object' && input.inputFeatureId
                ? input.inputFeatureId
                : typeof input === 'string'
                  ? input
                  : undefined,
            )
            .filter((id): id is string => Boolean(id))
        : [],
    ) ?? []
  );
}

/** Determines if a given input ID is used by any derived features other than the one being removed.
 *
 * @param inputId - The ID of the input to check
 * @param derivedFeatures - The current list of derived features in the template
 * @param removingFeatureId - The ID of the derived feature being removed
 * @returns True if the input is used by other derived features, false otherwise
 */
export function isInputUsedElsewhere(
  inputId: string,
  derivedFeatures: DerivedFeatureV1[],
  removingFeatureId: string,
): boolean {
  return derivedFeatures.some(
    (feature) =>
      feature.id !== removingFeatureId &&
      feature.rules?.some((rule) =>
        Array.isArray(rule.inputs)
          ? rule.inputs.some(
              (input) =>
                (typeof input === 'object' && input.inputFeatureId === inputId) ||
                (typeof input === 'string' && input === inputId),
            )
          : false,
      ),
  );
}

/** Updates the template's inputs by collecting all unique inputs from the derived features' rules,
 *  excluding any inputs that are also intermediates or that belong to a feature being removed, and are not used by
 *  other features.
 *
 * @param derivedFeatures - The current list of derived features in the template
 * @param columns - The full list of columns in the table
 * @param inputFeatures - The existing list of input features
 * @param removingFeature - An optional derived feature that is being removed
 * @returns The updated list of template inputs
 */
export function updateInputs(
  derivedFeatures: DerivedFeatureV1[],
  columns: ColumnDefinition[],
  inputFeatures: TemplateInput[],
  removingFeature?: DerivedFeatureV1,
): TemplateInput[] {
  // Collect all inputs from derived features' rules
  const allInputs = derivedFeatures.flatMap(
    (feature) =>
      feature.rules?.flatMap((rule) =>
        Array.isArray(rule.inputs)
          ? rule.inputs
              .map((input) => {
                if (typeof input === 'object' && input.inputFeatureId) {
                  return input;
                } else if (typeof input === 'string') {
                  const col = columns.find((c) => c.id === input);
                  if (col) {
                    return {
                      referenceId: col.id,
                      name: col.displayName,
                      isCollapsed: false,
                      description: col.description ?? '',
                      originalName: col.displayName,
                    };
                  }
                }
                return undefined;
              })
              .filter(Boolean)
          : [],
      ) ?? [],
  );

  // Remove inputs that belong to the removing feature and are not used elsewhere
  const removingFeatureInputIds = removingFeature ? getRemovingFeatureInputIds(removingFeature) : [];
  const remainingInputs = allInputs.filter((input) => {
    if (!input || typeof input !== 'object') return false;
    const inputId = (input as any).inputFeatureId ?? (input as any).referenceId;
    if (!inputId) return false;
    if (!removingFeatureInputIds.includes(inputId) || !removingFeature?.id) return true;
    return isInputUsedElsewhere(inputId, derivedFeatures, removingFeature.id);
  });

  // Remove inputs that are also intermediates
  const derivedNames = derivedFeatures.map((feature) =>
    'columnName' in feature && feature.columnName ? feature.columnName : feature.name,
  );
  const derivedIds = derivedFeatures.map((feature) =>
    'featureId' in feature && feature.featureId ? feature.featureId : feature.id,
  );
  const filteredInputs = remainingInputs.filter(
    (input) =>
      !(
        (input && 'inputFeatureId' in input && input.inputFeatureId && derivedIds.includes(input.inputFeatureId)) ||
        (input && 'referenceId' in input && input.referenceId && derivedIds.includes(input.referenceId)) ||
        (input?.name && derivedNames.includes(input.name))
      ),
  );

  // If a new input has an input feature id that matches an existing input feature, preserve the existing input feature
  // properties
  filteredInputs.forEach((input, index: number) => {
    const matchingInputFeature = inputFeatures.find((feature) =>
      'id' in feature && input !== undefined && 'inputFeatureId' in input
        ? feature.id === input?.inputFeatureId
        : false,
    );
    if (matchingInputFeature) {
      filteredInputs[index] = { ...matchingInputFeature } as unknown as TemplateRuleInputV1Object;
    }
  });

  // Preserve properties of existing inputs by replacing entries in filteredInputs with their counterparts from
  // inputFeatures when they refer to the same underlying table column. Match by `referenceId` (table-sourced
  // inputs synthesized above) or `columnDefinition.id` (template input features fetched/enriched with the
  // table application mapping in the edit-template flow).
  const mergedInputs = (filteredInputs as TemplateInput[]).map((input) => {
    if (!input.referenceId) {
      return input;
    }
    const inputColumnDefinitionId = (feature: TemplateInput) =>
      (feature as TemplateInput & { columnDefinition?: { id?: string } }).columnDefinition?.id;
    const existing = inputFeatures.find(
      (f) =>
        f.referenceId === input.referenceId ||
        ('columnDefinition' in f && inputColumnDefinitionId(f) === input.referenceId),
    );
    return existing ? existing : input;
  });

  return _.uniqBy(
    [
      // Put new items with a referenceId at the top of the list so they are easier to find in the UI
      ...mergedInputs.filter((input) => !!input.referenceId),
      ...mergedInputs.filter((input) => !input.referenceId),
    ],
    (input: TemplateInputIds) => input.inputFeatureId || input.referenceId || input.id,
  ) as unknown as TemplateInput[];
}

/** True for persisted template intermediate features during edit-template (not table-sourced column selections). */
const isPersistedTemplateIntermediateForEdit = (feature: TemplateOutput): boolean =>
  feature.isOutput === false &&
  feature.id !== undefined &&
  (!('displayName' in feature) ||
    'showInIntermediates' in feature ||
    'featureId' in feature ||
    'columnDefinition' in feature);

/**
 * Preserves the existing feature's template-format rules (`FeatureRuleV1` with `formulaCreator`,
 * resolved `scopedTo`, and `inputFeatureId` references on inputs) when the user's column edit
 * didn't alter the rule logic — only column-level metadata like the display name.
 *
 * The preview API returns `ColumnRuleOutputV1`-shaped rules that omit the `formulaCreator`
 * template object. If we replaced the existing feature's rules with those, the subsequent
 * template save would (1) fail with "Exactly one of argument, inputFeatureId, inputFeatureIndex,
 * or derivedFeatureIndex must be set" because `convertNewRules` would rebuild `scopedTo` from
 * the (possibly undefined) template-level scopedTo, and (2) lose the rule-input → input-feature
 * binding, surfacing previously-mapped inputs as "New input" in the UI.
 *
 * Returns `undefined` when preservation isn't safe (rule count differs, formula text changed,
 * input count changed, parameter list changed, input references changed, etc.), so the caller
 * falls back to the prior behavior of using the edited column's rules.
 */
const tryPreserveTemplateRulesForRename = (
  existingRules: FeatureRuleV1[] | undefined,
  editedRules: ColumnRuleOutputV1[] | undefined,
  newName: string | undefined,
  inputFeatures: TemplateInput[],
  derivedFeatures: TemplateOutput[],
  tableColumns: ColumnDefinition[],
): FeatureRuleV1[] | undefined => {
  if (!existingRules?.length || !editedRules?.length || existingRules.length !== editedRules.length) {
    return undefined;
  }

  const isTemplateFormulaCreatorRule = (
    rule: FeatureRuleV1 | undefined,
  ): rule is FeatureRuleV1 & { formulaCreator: TemplateFormulaCreatorRuleV1 } =>
    !!rule && 'formulaCreator' in rule && !!rule.formulaCreator && typeof rule.formulaCreator === 'object';

  // Only preserve for true rename-only edits: same rule type, formula text, input count,
  // parameter list, and resolved input references. Other structural changes (input swap/rebind,
  // added parameter, etc.) fall through to the regular flow.
  const canPreserve = existingRules.every((existingRule, i) => {
    const editedRule = editedRules[i];
    if (!isTemplateFormulaCreatorRule(existingRule) || !editedRule) {
      return false;
    }
    const existingFormula = existingRule.formulaCreator.formula?.argument;
    const editedFormula = editedRule.arguments?.formula;
    if (existingFormula !== editedFormula) {
      return false;
    }

    const existingInputCount = existingRule.inputs?.length ?? 0;
    const editedInputCount = editedRule.inputs?.length ?? 0;
    if (existingInputCount !== editedInputCount) {
      return false;
    }

    const existingParameters = getExistingFormulaCreatorParameterNames(existingRule.formulaCreator);
    const editedParameters = getEditedFormulaCreatorParameterNames(editedRule);
    if (!haveSameParameterNames(existingParameters, editedParameters)) {
      return false;
    }

    const existingInputReferences = resolveExistingRuleInputReferences(
      existingRule,
      inputFeatures,
      derivedFeatures,
      tableColumns,
    );
    const editedInputReferences = resolveEditedRuleInputReferences(editedRule);
    return haveSameResolvedInputReferences(existingInputReferences, editedInputReferences);
  });

  if (!canPreserve) {
    return undefined;
  }

  return existingRules.map((rule, ruleIndex) => {
    if (!isTemplateFormulaCreatorRule(rule)) {
      return rule;
    }
    const nameInput = rule.formulaCreator.name;
    // Only retarget the name argument when it was a literal value tied to the column. If the name
    // references an input/derived feature (inputFeatureId/inputFeatureIndex/derivedFeatureIndex),
    // the binding is intentional and should not be overwritten by the column rename.
    const isLiteralName = nameInput?.argument != null;
    const preservedNameArgument = getEditedFormulaCreatorNameArgument(editedRules[ruleIndex], tableColumns, newName);
    if (!preservedNameArgument || !isLiteralName) {
      return rule;
    }
    return {
      ...rule,
      formulaCreator: {
        ...rule.formulaCreator,
        name: { ...nameInput, argument: preservedNameArgument },
      },
    };
  });
};

/** Updates an edited derived feature in the template and recalculates inputs and intermediates accordingly.
 *
 * @param featureId
 * @param derivedFeatures
 * @param inputFeatures
 * @param editedColumn
 * @param tableColumns
 */
export function updateEditedDerivedFeature(
  featureId: string | undefined,
  derivedFeatures: TemplateOutput[],
  inputFeatures: TemplateInput[],
  editedColumn: ColumnDefinition | undefined,
  tableColumns: ColumnDefinition[],
) {
  if (!featureId || !editedColumn) {
    return;
  }

  const featureIndex = derivedFeatures.findIndex(
    (feature) => feature.id === featureId || ('featureId' in feature && feature.featureId === featureId),
  );
  if (featureIndex === -1) {
    return;
  }

  const existingFeature = derivedFeatures[featureIndex];
  if (isPersistedTemplateIntermediateForEdit(existingFeature)) {
    const existingPersistedFeature = existingFeature as unknown as DerivedFeatureV1 & {
      showInIntermediates?: boolean;
      columnDefinition?: Pick<ColumnDefinition, 'id'>;
    };
    const newIntermediateName = editedColumn.displayName ?? editedColumn.columnName ?? existingPersistedFeature.name;
    const preservedIntermediateRules = tryPreserveTemplateRulesForRename(
      existingPersistedFeature.rules as FeatureRuleV1[] | undefined,
      editedColumn.rules as ColumnRuleOutputV1[] | undefined,
      newIntermediateName,
      inputFeatures,
      derivedFeatures,
      tableColumns,
    );
    const editedIntermediate = {
      ...existingPersistedFeature,
      name: newIntermediateName,
      rules:
        preservedIntermediateRules ??
        ((editedColumn.rules ?? existingPersistedFeature.rules) as unknown as FeatureRuleV1[]),
      showInIntermediates: true,
      ...(isFeatureTypeDerivedFeatureWithColumnDefinition(existingPersistedFeature) && {
        columnDefinition: { id: editedColumn.id },
      }),
    };

    const updatedDerivedFeatures = [
      ...derivedFeatures.slice(0, featureIndex),
      editedIntermediate,
      ...derivedFeatures.slice(featureIndex + 1),
    ] as TemplateOutput[];

    const finalInputs = exposedForTesting.updateInputs(
      updatedDerivedFeatures as unknown as DerivedFeatureV1[],
      tableColumns,
      inputFeatures as unknown as TemplateInput[],
    );

    setTemplate(TEMPLATE_TYPES.SCALING, {
      derivedFeatures: updatedDerivedFeatures,
      inputFeatures: finalInputs as unknown as TemplateInput[],
    });
    return;
  }

  const existingOutputAsDerived = existingFeature as unknown as DerivedFeatureV1;
  const existingOutputName = existingOutputAsDerived.name;
  const newOutputName = editedColumn.displayName ?? editedColumn.columnName ?? existingOutputName;
  const preservedOutputRules = tryPreserveTemplateRulesForRename(
    existingOutputAsDerived.rules as FeatureRuleV1[] | undefined,
    editedColumn.rules as ColumnRuleOutputV1[] | undefined,
    newOutputName,
    inputFeatures,
    derivedFeatures,
    tableColumns,
  );

  // Rename-only edit (formula and dependencies unchanged): update the existing output in place,
  // preserving its identity, existing (non-table-sourced) shape, and array position. Reordering it
  // to the front or giving it a table-sourced `displayName` would make formatTemplateForSave bucket
  // it as a "new derived" feature ahead of the existing intermediates it depends on, emitting a
  // forward derivedFeatureIndex the backend can't build (CRAB-58787). A real formula/structure edit
  // (preservedOutputRules == null) still uses the recompute path below, since deps may have changed.
  if (preservedOutputRules) {
    const renamedOutput = {
      ...existingOutputAsDerived,
      name: newOutputName,
      rules: preservedOutputRules,
    };
    const updatedDerivedFeatures = [
      ...derivedFeatures.slice(0, featureIndex),
      renamedOutput,
      ...derivedFeatures.slice(featureIndex + 1),
    ] as TemplateOutput[];
    const finalInputs = exposedForTesting.updateInputs(
      updatedDerivedFeatures as unknown as DerivedFeatureV1[],
      tableColumns,
      inputFeatures as unknown as TemplateInput[],
    );
    setTemplate(TEMPLATE_TYPES.SCALING, {
      derivedFeatures: updatedDerivedFeatures,
      inputFeatures: finalInputs as unknown as TemplateInput[],
    });
    return;
  }

  // preservedOutputRules is always null here (the rename-only case returned above), so the edited
  // column's rules are used as-is — this branch handles genuine formula/structure edits.
  const editedFeature = {
    ...editedColumn,
    featureId,
    isOutput: derivedFeatures[featureIndex].isOutput,
    ...(existingOutputAsDerived.canonicalId && { canonicalId: existingOutputAsDerived.canonicalId }),
  };

  const updatedDerivedFeatures = [
    ...derivedFeatures.slice(0, featureIndex),
    editedFeature,
    ...derivedFeatures.slice(featureIndex + 1),
  ] as TemplateOutput[];

  const editedOutputsAndTheirIntermediates = exposedForTesting.getUpdatedIntermediates(
    updatedDerivedFeatures.filter((f) => 'isOutput' in f && f.isOutput === false).map((f) => f.id) as string[],
    updatedDerivedFeatures as unknown as DerivedFeatureV1[],
  );

  const updatedInputsAndOutputs = getUpdatedInputsAndOutputs(
    [editedColumn],
    inputFeatures,
    editedOutputsAndTheirIntermediates as unknown as TemplateOutput[],
    tableColumns,
  );

  const formattedOutputs = updatedInputsAndOutputs?.outputs?.length
    ? updatedInputsAndOutputs.outputs.map((f) =>
        'isOutput' in f && f.isOutput === false ? { ...f, showInIntermediates: true } : f,
      )
    : [];

  // Get the formatted version of the edited column to make sure it has the isOutput field set correctly
  const formattedEditedColumn = updatedDerivedFeatures.find((f) => f.id === editedFeature.id);
  const allDerivedFeatures = [formattedEditedColumn, ...formattedOutputs];
  const finalDerivedFeatures = _.uniqBy(allDerivedFeatures, (f) => f?.id ?? '') as unknown as TemplateOutput[];
  const finalInputs = exposedForTesting.updateInputs(
    finalDerivedFeatures as unknown as DerivedFeatureV1[],
    tableColumns,
    inputFeatures as unknown as TemplateInput[],
  );

  setTemplate(TEMPLATE_TYPES.SCALING, {
    derivedFeatures: finalDerivedFeatures,
    inputFeatures: finalInputs as unknown as TemplateInput[],
  });
}

export function mapDerivedFeature(
  intermediateId: string,
  isMapped: boolean,
  mappedFeatureId: string | undefined,
  newDerivedFeatures: NewlyAddedDerivedFeature[],
): NewlyAddedDerivedFeature[] {
  return newDerivedFeatures.map((feature) => {
    if ((feature.referenceId ?? feature.id) !== intermediateId) return feature;
    return isMapped
      ? { ...feature, isMapped: true, mappedFeatureId }
      : { ...feature, isMapped: false, mappedFeatureId: undefined };
  });
}

/**
 * Saves the current edits to the draft without locking or publishing.
 * The draft remains editable for further changes.
 */
// TODO(CRAB-58652): Extract to editModel.actions.ts — side-effecting orchestration, not a pure utility.
export async function handleSaveDraftInToolPane(
  templateId: string,
  versionId: string,
  formattedTemplate: TemplateInputV1,
  workbookId: string,
) {
  await saveDraft(templateId, versionId, formattedTemplate);
  successToast({ messageKey: 'TEMPLATES.SAVE_DRAFT_SUCCESS' });
  resetTemplate(TEMPLATE_TYPES.TOOL);
  setActiveTool(TREND_TOOLS.MODELS);
  await fetchTemplates([workbookId]);
}

export const toNewlyAddedInputFeature = (feature: TemplateInput): NewlyAddedInputFeature => {
  const id = feature.id ?? feature.referenceId ?? '';
  return {
    ...feature,
    id,
    referenceId: id,
  };
};

/** Creates maps for lookups of intermediate features in new and existing derived feature lists. */
const createIntermediateLookupMaps = (
  existingDerivedFeatures: TemplateDerivedFeature[],
  newDerivedFeatures: NewlyAddedDerivedFeature[],
) => {
  const existingIntermediatesById = new Map(
    existingDerivedFeatures.filter((f) => !f.isOutput && f.id).map((f) => [f.id!, f]),
  );
  const newIntermediatesByRefId = new Map(
    newDerivedFeatures
      .filter((f) => !f.isOutput && !f.intermediateAsInput && !f.mappedFeatureId)
      .map((f) => [f.referenceId!, f]),
  );
  return { existingIntermediatesById, newIntermediatesByRefId };
};

/** Recursively collects the IDs of all features that the given feature depends on. */
const collectDerivedFeatureDependencies = (
  feature: TemplateDerivedFeature | NewlyAddedDerivedFeature,
  newDerivedFeatures: NewlyAddedDerivedFeature[],
  existingDerivedFeatures: TemplateDerivedFeature[],
): Set<string> => {
  const usedFeatureIdsForFeature = new Set<string>();

  const { existingIntermediatesById, newIntermediatesByRefId } = createIntermediateLookupMaps(
    existingDerivedFeatures,
    newDerivedFeatures,
  );

  const collectRecursive = (currentFeature: TemplateDerivedFeature | NewlyAddedDerivedFeature) => {
    currentFeature.rules?.forEach((rule) => {
      if (!Array.isArray(rule.inputs)) return;

      rule.inputs.forEach((input) => {
        const featureId = getFeatureIdFromInput(input);
        if (!featureId || usedFeatureIdsForFeature.has(featureId)) return;

        usedFeatureIdsForFeature.add(featureId);

        const intermediate = existingIntermediatesById.get(featureId) ?? newIntermediatesByRefId.get(featureId);
        if (intermediate) {
          collectRecursive(intermediate);
        }
      });
    });
  };

  collectRecursive(feature);
  return usedFeatureIdsForFeature;
};

/** Collects the IDs of all features that are used by the given output features, including their dependencies. */
const collectAllUsedFeatureIds = (
  outputs: Array<TemplateDerivedFeature | NewlyAddedDerivedFeature>,
  newDerivedFeatures: NewlyAddedDerivedFeature[],
  existingDerivedFeatures: TemplateDerivedFeature[],
): Set<string> => {
  return new Set(
    outputs.flatMap((feature) => [
      ...collectDerivedFeatureDependencies(feature, newDerivedFeatures, existingDerivedFeatures),
    ]),
  );
};

/** After editing or deleting a derived feature, some features may no longer be used. This function collects the
 *  dependencies of all existing and new output features and removes any features that are not depended on by any
 *  output. */
export const filterUnusedFeatures = (
  existingDerivedFeatures: TemplateDerivedFeature[],
  existingInputFeatures: TemplateInput[],
  newDerivedFeatures: NewlyAddedDerivedFeature[],
  newInputFeatures: NewlyAddedInputFeature[],
) => {
  // Enrich rule inputs with featureId so dependency tracking works correctly
  const enrichedNewDerivedFeatures = enrichNewDerivedFeaturesWithFeatureId(newDerivedFeatures, newInputFeatures);

  const existingOutputs = existingDerivedFeatures.filter((f) => f.isOutput);
  const newOutputs = enrichedNewDerivedFeatures.filter((f) => f.isOutput);

  const allOutputs = [...existingOutputs, ...newOutputs];
  const allUsedFeatureIds = collectAllUsedFeatureIds(allOutputs, enrichedNewDerivedFeatures, existingDerivedFeatures);

  // Keep existing intermediates that are mapping targets
  enrichedNewDerivedFeatures
    .filter((feature) => feature.mappedFeatureId)
    .forEach((feature) => allUsedFeatureIds.add(feature.mappedFeatureId!));

  const isUsed = (id?: string) => id && allUsedFeatureIds.has(id);

  const usedExistingDerivedFeatures = existingDerivedFeatures.filter((f) => f.isOutput !== false || isUsed(f.id));
  // Pruning existing inputs is deferred to formatTemplateInput so we don't destroy identity
  // before the user has a chance to re-reference an input via mappedFeatureId (CRAB-58728).
  const usedExistingInputFeatures = existingInputFeatures;
  const usedNewDerivedFeatures = enrichedNewDerivedFeatures.filter((f) => f.isOutput || isUsed(f.referenceId));
  const usedNewInputFeatures = newInputFeatures.filter((f) => isUsed(f.referenceId));

  return {
    usedExistingDerivedFeatures,
    usedExistingInputFeatures,
    usedNewDerivedFeatures,
    usedNewInputFeatures,
  };
};

/** Checks if any feature in the list references the target derivedFeatureIndex. */

/** Deletes an existing output feature and updates the template's derived features and inputs accordingly. If the output is
 * used by other existing features, it will be converted into an intermediate instead of being deleted to avoid breaking dependencies, and unused intermediates will be removed.*/
export function deleteExistingOutputFeatureAndUpdateDependencies(
  outputId: string,
  existingDerivedFeatures: TemplateDerivedFeature[],
  existingInputFeatures: TemplateInput[],
  newDerivedFeatures: NewlyAddedDerivedFeature[],
  newInputFeatures: NewlyAddedInputFeature[],
  templateType: TemplateType,
) {
  if (!existingDerivedFeatures?.length) return;

  const featureToRemove = existingDerivedFeatures.find((f) => f.id === outputId);
  if (!featureToRemove) return;

  // Convert to intermediate if used by other existing outputs
  // Note: We only check existing features because new features reference other features via indices
  // (inputFeatureIndex, derivedFeatureIndex), not via IDs (inputFeatureId). New features cannot
  // reference existing features by design, so there's no risk of dangling dependencies.
  if (exposedForTesting.isFeatureUsedByOtherOutputs(outputId, existingDerivedFeatures)) {
    setTemplate(templateType, {
      existingDerivedFeatures: existingDerivedFeatures.map((feature) =>
        feature.id === outputId ? { ...feature, isOutput: false } : feature,
      ),
    });
    return;
  }

  // Remove the output and use the unified removal function to clean up unused dependencies
  const remainingExisting = existingDerivedFeatures.filter((f) => f.id !== outputId);
  const allInUseFeatures = filterUnusedFeatures(
    remainingExisting,
    existingInputFeatures,
    newDerivedFeatures,
    newInputFeatures,
  );

  setTemplate(templateType, {
    existingDerivedFeatures: allInUseFeatures.usedExistingDerivedFeatures,
    existingInputFeatures: allInUseFeatures.usedExistingInputFeatures,
    newDerivedFeatures: allInUseFeatures.usedNewDerivedFeatures,
    newInputFeatures: allInUseFeatures.usedNewInputFeatures,
  });
}

/** Rewrites each parameter's name (and the corresponding `$identifier` tokens in the formula string)
 * to a lowerCamelCase identifier derived from the Feature's current display name. */
export function deriveNameBasedParameterIdentifiers(
  formula: FormulaOutput,
  resolvedFeatureNames: string[],
): FormulaOutput {
  const parameters = formula.parameters ?? [];
  const usedIdentifiers = new Set<string>();
  const identifierByOldName = new Map<string, string>();

  const newParameters = parameters.map((param, i) => {
    const oldId = param.name ?? '';
    // Fall back to the backend's alphabetic identifier when the Feature can't be resolved.
    const candidate = featureNameToIdentifier(resolvedFeatureNames[i] ?? '', oldId);
    const finalId = makeUniqueIdentifier(candidate, usedIdentifiers);
    usedIdentifiers.add(finalId);
    identifierByOldName.set(oldId, finalId);

    return { ...param, name: finalId };
  });

  return {
    ...formula,
    formula: renameFormulaVariables(formula.formula ?? '', identifierByOldName),
    parameters: newParameters,
  };
}

/** Converts a formula into the format expected by the backend for a formula creator rule */
export const toFormulaCreatorRule = (formula: FormulaOutput, featureName: string): TemplateFormulaCreatorRuleV1 => {
  const parameterNames = (formula.parameters ?? [])
    .map((parameter) => parameter.name)
    .filter((name): name is string => !!name);
  const makeStringInput = (name: string, argument: string): TemplateRuleInputV1String => ({
    name,
    type: TemplateRuleInputV1StringTypeEnum.TEXT,
    argument,
  });
  const makeListStringInput = (name: string, argument: string[]): TemplateRuleInputV1ListString => ({
    name,
    type: TemplateRuleInputV1ListStringTypeEnum.TEXT,
    argument,
  });
  const makeUuidInput = (name: string, argument: string): TemplateRuleInputV1UUID => ({
    name,
    type: TemplateRuleInputV1UUIDTypeEnum.ITEMID,
    argument,
  });

  return {
    name: makeStringInput('name', featureName),
    formula: makeStringInput('formula', formula.formula ?? ''),
    parameters: makeListStringInput('parameters', parameterNames),
    scopedTo: formula.scopedTo ? makeUuidInput('scopedTo', formula.scopedTo) : undefined,
    variableParameters: makeListStringInput('variableParameters', []),
    variableParameterStrings: makeListStringInput('variableParameterStrings', []),
  };
};

/** Builds backend-formatted rules for each derived feature by combining its saved formula with
 * the template's full input list. Features without a matching formula in the map are returned as-is. */
export function formatDerivedFeaturesUsingFormulas(
  derivedFeatures: NewlyAddedDerivedFeature[],
  inputFeatures: NewlyAddedInputFeature[],
  formulasByFeatureId: Map<string, FormulaOutput>,
  allDerivedForIndexLookup?: NewlyAddedDerivedFeature[],
): NewlyAddedDerivedFeature[] {
  // Use allDerivedForIndexLookup if provided, otherwise use derivedFeatures
  const derivedForLookup = allDerivedForIndexLookup ?? derivedFeatures;

  return derivedFeatures.map((feature) => {
    const featureId = getDerivedFeatureId(feature);
    if (!featureId) return feature;

    const formula = formulasByFeatureId.get(featureId);
    if (formula) {
      const ruleInputs = mapRuleInputsToFeatures(
        (formula.parameters ?? []).map((parameter) => parameter.item?.id) as string[],
        inputFeatures,
        derivedForLookup as TemplateOutput[],
        TemplateRuleInputV1ObjectTypeEnum.FEATUREID,
      );
      const resolvedFeatureNames = ruleInputs.map((input) => input.name ?? '');
      const formulaWithDerivedIdentifiers = deriveNameBasedParameterIdentifiers(formula, resolvedFeatureNames);
      const rules: FeatureRuleV1[] = [
        {
          inputs: ruleInputs as TemplateRuleInputV1Object[],
          formulaCreator: toFormulaCreatorRule(formulaWithDerivedIdentifiers, feature.name),
        },
      ];
      return { ...feature, rules };
    }

    if (!feature.rules?.length) return feature;

    const reindexedRules = reindexRuleInputsForMappedFeatures(feature.rules, inputFeatures, derivedForLookup);

    return { ...feature, rules: reindexedRules };
  });
}

/** Depending on whether the feature being updated is a newly added feature that has not been saved yet or an
 *  existing feature, updates the correct list of features and recalculates dependencies accordingly by updating
 *  inputs and derived features. */
export const updateInputFeature = (
  patch: Partial<TemplateInput>,
  input: TemplateInput,
  newInputFeatures: NewlyAddedInputFeature[],
  existingInputFeatures: TemplateInput[],
  isNewlyAdded = false,
  templateType: TemplateType = TEMPLATE_TYPES.TOOL,
) => {
  const inputId = input.referenceId ?? input.id;
  if (!inputId) {
    return;
  }
  if (isNewlyAdded) {
    const updatedNewFeatures = newInputFeatures.map((feature) =>
      feature.referenceId === inputId ? { ...feature, ...patch } : feature,
    );
    setTemplate(templateType, { newInputFeatures: updatedNewFeatures });
    return;
  }
  const updatedExistingFeatures = existingInputFeatures.map((feature) =>
    feature.id === inputId ? { ...feature, ...patch } : feature,
  );
  setTemplate(templateType, {
    existingInputFeatures: updatedExistingFeatures,
  });
};

const toFormattedInputFeature = (feature: NewlyAddedInputFeature | TemplateInput) => ({
  name: feature.name,
  description: feature.description,
  outputType: OutputTypeEnum.ITEMID,
  id: 'referenceId' in feature ? '' : feature.id,
  canonicalId: 'referenceId' in feature ? undefined : feature.canonicalId,
  // Only include defined restrictions; omitting them clears the restriction.
  ...(feature.outputItemType !== undefined && { outputItemType: feature.outputItemType }),
  ...(feature.outputItemMetadata !== undefined && { outputItemMetadata: feature.outputItemMetadata }),
});

/** Strips featureId from rule inputs before sending to the backend */
const stripFeatureIdFromInputs = (rules: FeatureRuleV1[] | undefined): FeatureRuleV1[] => {
  if (!rules) return [];
  return rules.map((rule) => ({
    ...rule,
    inputs: Array.isArray(rule.inputs)
      ? rule.inputs.map((input) => {
          if (typeof input === 'object' && 'featureId' in input) {
            const { featureId, ...rest } = input as TemplateRuleInputWithFeatureId;
            return rest;
          }
          return input;
        })
      : rule.inputs,
  }));
};

const toFormattedDerivedFeature = (feature: NewlyAddedDerivedFeature): DerivedFeatureV1 => ({
  id: '',
  name: feature.name,
  rules: stripFeatureIdFromInputs(feature.rules),
  outputType: OutputTypeEnum.ITEMID,
  isOutput: feature.isOutput,
  outputItemType: feature.outputItemType,
  outputItemMetadata: feature.outputItemMetadata,
});

/** Strips UI-only fields from TemplateDerivedFeature to get a clean DerivedFeatureV1 for the backend */
const stripUIFields = (feature: TemplateDerivedFeature): DerivedFeatureV1 => ({
  id: feature.id,
  canonicalId: feature.canonicalId,
  name: feature.name,
  rules: stripFeatureIdFromInputs(feature.rules),
  outputType: feature.outputType,
  isOutput: feature.isOutput,
  outputItemType: feature.outputItemType,
  outputItemMetadata: feature.outputItemMetadata,
});

const buildFormattedTemplate = ({
  name,
  description,
  templateId,
  scopedTo,
  inputFeatures,
  instanceNameFeature,
  newDerivedFeatures,
  existingDerivedFeatures,
}: {
  name: string;
  description: string;
  templateId: string;
  scopedTo?: string;
  inputFeatures: TemplateInput[];
  instanceNameFeature?: TemplateInput;
  newDerivedFeatures: DerivedFeatureV1[];
  existingDerivedFeatures: TemplateDerivedFeature[];
}) => {
  const formattedInputFeatures = inputFeatures.map((feature) => toFormattedInputFeature(feature));
  const inputsIncludingInstanceName = [
    ...formattedInputFeatures,
    ...(instanceNameFeature ? [instanceNameFeature] : []),
  ];

  return {
    name,
    description,
    id: templateId,
    scopedTo,
    derivedFeatures: [...newDerivedFeatures, ...existingDerivedFeatures.map(stripUIFields)],
    inputFeatures: inputsIncludingInstanceName,
  };
};

/** Existing InputFeature ids still referenced by the draft — directly by an existing
 *  derived feature's rule, or indirectly via a new input's mappedFeatureId alias. */
export const getReferencedExistingInputIds = (
  existingDerivedFeatures: TemplateDerivedFeature[],
  newInputFeatures: NewlyAddedInputFeature[],
): Set<string> => {
  const ids = new Set<string>();
  existingDerivedFeatures.forEach((df) =>
    df.rules?.forEach((rule) =>
      rule.inputs?.forEach((input) => {
        if (input.inputFeatureId) ids.add(input.inputFeatureId);
      }),
    ),
  );
  newInputFeatures.forEach((f) => {
    if (f.mappedFeatureId) ids.add(f.mappedFeatureId);
  });
  return ids;
};

/**
 * Reindexes rule inputs to match an input array, handling mapped inputs.
 * When an input is mapped, uses the mapped feature's index instead.
 */
function reindexRuleInputsForMappedFeatures(
  rules: FeatureRuleV1[] | undefined,
  inputFeatures: (TemplateInput | NewlyAddedInputFeature)[],
  derivedForLookup?: NewlyAddedDerivedFeature[],
): FeatureRuleV1[] | undefined {
  if (!rules) return rules;

  return rules.map((rule) => ({
    ...rule,
    inputs: rule.inputs?.map((input) => {
      if (typeof input !== 'object' || !hasFeatureId(input)) return input;

      let inputIndex = inputFeatures.findIndex((f) => (f.referenceId ?? f.id) === input.featureId);

      // If the input has mappedFeatureId, use the mapped feature's index instead
      if (inputIndex >= 0) {
        const foundInput = inputFeatures[inputIndex];
        if (foundInput.mappedFeatureId) {
          const mappedIndex = inputFeatures.findIndex((f) => f.id === foundInput.mappedFeatureId);
          if (mappedIndex >= 0) {
            inputIndex = mappedIndex;
          }
        }
      }

      const derivedIndex = derivedForLookup
        ? derivedForLookup.findIndex((f) => (f.referenceId ?? f.id) === input.featureId)
        : -1;

      return {
        ...input,
        ...(inputIndex >= 0 && { inputFeatureIndex: inputIndex }),
        ...(derivedIndex >= 0 && { derivedFeatureIndex: derivedIndex }),
      };
    }),
  }));
}

/**
 * Reindexes existing derived features to match a reordered input array.
 * Handles mapped inputs by updating indices to point to the mapped feature.
 */
export function reindexExistingDerivedFeatures(
  existingDerivedFeatures: TemplateDerivedFeature[],
  sortedInputFeatures: TemplateInput[],
): TemplateDerivedFeature[] {
  return existingDerivedFeatures.map((feature) => ({
    ...feature,
    rules: reindexRuleInputsForMappedFeatures(feature.rules, sortedInputFeatures) ?? feature.rules,
  }));
}

/**
 * Identifies mapped intermediates in the derived features array and returns their indices
 * and a mapping from each index to the existing feature it maps to.
 */
function getMappedIntermediateInfo(
  newDerivedFeatures: NewlyAddedDerivedFeature[],
  existingDerivedFeatures: TemplateDerivedFeature[],
): {
  mappedIndices: Set<number>;
  mappedIndexToExistingFeature: Map<number, TemplateDerivedFeature>;
} {
  const mappedIndices = new Set<number>();
  const mappedIndexToExistingFeature = new Map<number, TemplateDerivedFeature>();
  const existingById = new Map(existingDerivedFeatures.filter((f) => f.id).map((f) => [f.id!, f]));

  newDerivedFeatures.forEach((feature, index) => {
    if (feature.mappedFeatureId && !feature.isOutput) {
      const existingFeature = existingById.get(feature.mappedFeatureId);
      if (existingFeature) {
        mappedIndices.add(index);
        mappedIndexToExistingFeature.set(index, existingFeature);
      }
    }
  });

  return { mappedIndices, mappedIndexToExistingFeature };
}

/**
 * Converts derivedFeatureIndex references that point to mapped intermediates into
 * inputFeatureId references, and removes the derivedFeatureIndex from those inputs.
 * Non-mapped references are left unchanged (reindexNewDerivedFeatures handles them).
 */
function convertMappedFeatureIndexToInputFeatureId(
  features: NewlyAddedDerivedFeature[],
  mappedIndices: Set<number>,
  mappedIndexToExistingFeature: Map<number, TemplateDerivedFeature>,
): NewlyAddedDerivedFeature[] {
  return features.map((feature) => ({
    ...feature,
    rules: feature.rules?.map((rule) => ({
      ...rule,
      inputs: rule.inputs?.map((input) => {
        if (
          typeof input === 'object' &&
          input.derivedFeatureIndex !== undefined &&
          mappedIndices.has(input.derivedFeatureIndex)
        ) {
          const existingFeature = mappedIndexToExistingFeature.get(input.derivedFeatureIndex);
          if (existingFeature?.id) {
            const { derivedFeatureIndex, ...baseInput } = input;
            return { ...baseInput, inputFeatureId: existingFeature.id, name: existingFeature.name };
          }
        }
        return input;
      }),
    })),
  }));
}

/**
 * Resolves mapped intermediates by removing them from the array and rewriting
 * rule inputs that reference them. References to mapped intermediates are converted
 * from derivedFeatureIndex (array position) to inputFeatureId (existing intermediate ID).
 * Returns both the filtered derived features and the cleaned inputs.
 *
 * @param newDerivedFeatures - The array of newly added derived features, which may contain mapped intermediates.
 * @param existingDerivedFeatures - The array of existing derived features, used for dependency lookups when filtering unused features.
 * @param newInputFeatures - The array of newly added input features, used for dependency lookups when filtering unused features and for reindexing.
 * @param inputFeaturesForReindex - Optional combined input features array used for reindexing.
 *   When omitted, uses newInputFeatures. Pass the full array (new + existing) when derived
 *   feature rules reference existing inputs.
 */
export function resolveMappedIntermediates(
  newDerivedFeatures: NewlyAddedDerivedFeature[],
  existingDerivedFeatures: TemplateDerivedFeature[],
  newInputFeatures: NewlyAddedInputFeature[],
  inputFeaturesForReindex?: NewlyAddedInputFeature[],
): { derivedFeatures: NewlyAddedDerivedFeature[]; inputFeatures: NewlyAddedInputFeature[] } {
  const { mappedIndices, mappedIndexToExistingFeature } = getMappedIntermediateInfo(
    newDerivedFeatures,
    existingDerivedFeatures,
  );

  if (mappedIndices.size === 0) {
    return { derivedFeatures: newDerivedFeatures, inputFeatures: newInputFeatures };
  }

  const convertedIndexes = convertMappedFeatureIndexToInputFeatureId(
    newDerivedFeatures,
    mappedIndices,
    mappedIndexToExistingFeature,
  );
  const filtered = convertedIndexes.filter((_, index) => !mappedIndices.has(index));
  const reindexed = reindexNewDerivedFeatures(filtered, inputFeaturesForReindex ?? newInputFeatures);

  const { usedNewDerivedFeatures, usedNewInputFeatures } = filterUnusedFeatures(
    existingDerivedFeatures,
    [],
    reindexed,
    newInputFeatures,
  );

  return { derivedFeatures: usedNewDerivedFeatures, inputFeatures: usedNewInputFeatures };
}

/** Builds the API input shape for editing a template from the EditModelTool's working state.
 *
 * When the template contains mapped inputs, derived feature rules are recalculated using each
 * feature's saved formula. The formulas are passed in via `formulasByFeatureId` (see
 * {@link fetchFormulasForFeatures} for fetching them). When no mapped inputs exist, the formula
 * map isn't consulted — pass an empty map.
 *
 * Callers are responsible for fetching the formula map and persisting the returned input. */
export const formatTemplateInput = ({
  name,
  description,
  templateId,
  scopedTo,
  newInputFeatures,
  existingInputFeatures,
  instanceNameFeature,
  newDerivedFeatures,
  existingDerivedFeatures,
  formulasByFeatureId,
}: {
  name: string;
  description: string;
  templateId: string;
  scopedTo?: string;
  newInputFeatures: NewlyAddedInputFeature[];
  existingInputFeatures: TemplateInput[];
  instanceNameFeature?: TemplateInput;
  newDerivedFeatures: NewlyAddedDerivedFeature[];
  existingDerivedFeatures: TemplateDerivedFeature[];
  formulasByFeatureId: Map<string, FormulaOutput>;
}): TemplateInputV1 => {
  // Save-time cleanup for the edit-time deferral in filterUnusedFeatures: drop any existing
  // input no rule or alias references, otherwise the server rejects the template as invalid.
  const referencedExistingInputIds = getReferencedExistingInputIds(existingDerivedFeatures, newInputFeatures);
  const referencedExistingInputFeatures = existingInputFeatures.filter(
    (f) => f.id !== undefined && referencedExistingInputIds.has(f.id),
  );

  const hasMappedInputs = [...newInputFeatures, ...referencedExistingInputFeatures].some(
    (feature) => feature.mappedFeatureId,
  );

  // Filter out intermediates toggled to inputs, resolve mapped intermediates, and clean up orphans
  const derivedFeaturesToSave = newDerivedFeatures.filter((f) => !f.intermediateAsInput);
  const combinedInputsForReindex = [
    ...newInputFeatures,
    ...referencedExistingInputFeatures.map(toNewlyAddedInputFeature),
  ];
  // Reindex after filtering to fix derivedFeatureIndex references
  const reindexedAfterFiltering = reindexNewDerivedFeatures(derivedFeaturesToSave, combinedInputsForReindex);
  const { derivedFeatures: resolvedDerivedFeatures, inputFeatures: cleanedNewInputs } = resolveMappedIntermediates(
    reindexedAfterFiltering,
    existingDerivedFeatures,
    newInputFeatures,
    combinedInputsForReindex,
  );

  if (!hasMappedInputs) {
    return buildFormattedTemplate({
      name,
      description,
      templateId,
      scopedTo,
      inputFeatures: [...cleanedNewInputs, ...referencedExistingInputFeatures],
      instanceNameFeature,
      newDerivedFeatures: resolvedDerivedFeatures.map(toFormattedDerivedFeature),
      existingDerivedFeatures,
    });
  }

  const sortedNewInputs = [
    ...cleanedNewInputs.filter((feature) => !feature.mappedFeatureId),
    ...cleanedNewInputs.filter((feature) => feature.mappedFeatureId),
  ];
  const sortedNewAndExistingInputFeatures = [...referencedExistingInputFeatures, ...sortedNewInputs];

  const derivedFeaturesWithRebuiltFormulas = formatDerivedFeaturesUsingFormulas(
    resolvedDerivedFeatures,
    sortedNewAndExistingInputFeatures.map(toNewlyAddedInputFeature),
    formulasByFeatureId,
  );

  const formattedNewDerivedFeatures = resolveMappedIntermediates(
    derivedFeaturesWithRebuiltFormulas,
    existingDerivedFeatures,
    cleanedNewInputs,
  ).derivedFeatures.map(toFormattedDerivedFeature);

  const allInUseInputFeatures = [
    ...referencedExistingInputFeatures,
    ...cleanedNewInputs.filter((feature) => !feature.mappedFeatureId),
  ];

  const reindexedExistingDerivedFeatures = reindexExistingDerivedFeatures(
    existingDerivedFeatures,
    allInUseInputFeatures,
  );

  return buildFormattedTemplate({
    name,
    description,
    templateId,
    scopedTo,
    inputFeatures: allInUseInputFeatures,
    instanceNameFeature,
    newDerivedFeatures: formattedNewDerivedFeatures,
    existingDerivedFeatures: reindexedExistingDerivedFeatures,
  });
};

/** Converts newly added derived features into options to be displayed in the Formula Item Table
 *
 * @param features - The newly added features to convert to options
 * @param isOutput - Whether to format outputs (true) or intermediates (false)
 */
const formatNewlyAddedFeaturesForDisplay = (
  features: NewlyAddedDerivedFeature[],
  isOutput: boolean,
): FormulaItemTableOption[] => {
  return features
    .filter((feature) => {
      if (isOutput) {
        return feature.isOutput;
      }
      return !feature.isOutput && !feature.intermediateAsInput;
    })
    .map((f) => {
      const allFormulas = (f.rules ?? []).map((rule) => {
        const formula = rule.formulaCreator?.formula?.argument ?? '';
        const paramArgs: string[] = Array.isArray(rule.formulaCreator?.parameters?.argument)
          ? rule.formulaCreator.parameters.argument
          : [];
        const inputs: TemplateRuleInputV1Object[] = Array.isArray(rule.inputs) ? rule.inputs : [];

        const parameters = inputs.map((input: TemplateRuleInputV1Object, idx: number) => {
          return {
            name: input.name,
            identifier: paramArgs[idx] ?? input.name ?? '',
          };
        });
        return { formula, parameters };
      });

      return {
        key: isOutput ? f.id! : f.referenceId!,
        display: f.name,
        allFormulas,
        isNew: true,
        isMapped: f.isMapped,
        mappedFeatureId: f.mappedFeatureId,
        outputItemType: f.outputItemType,
        outputItemMetadata: f.outputItemMetadata,
      };
    });
};

/** Converts newly added intermediate features into options to be displayed in the Formula Item Table
 * @param features - The newly added features to convert to options
 */
export const formatNewlyAddedIntermediatesForDisplay = (
  features: NewlyAddedDerivedFeature[],
): FormulaItemTableOption[] => formatNewlyAddedFeaturesForDisplay(features, false);

/** Converts newly added output features into options to be displayed in the Formula Item Table
 * @param features - The newly added features to convert to options
 */
export const formatNewlyAddedOutputsForDisplay = (features: NewlyAddedDerivedFeature[]): FormulaItemTableOption[] =>
  formatNewlyAddedFeaturesForDisplay(features, true);

/**
 * Updates an existing derived feature with new formula and parameter mappings.
 * Maps parameters to proper rule inputs with correct indices for input/derived features.
 * @param featureToEdit The existing derived feature being edited
 * @param newFormula The new formula to update the feature with
 * @param parameterMappings The selected parameter mappings from the formula editor, containing the mapped items and their corresponding formula parameter identifiers
 * @param allNewInputs All newly added input features in the template, used to find the correct indices for mapped input features
 * @param allNewDerived All newly added derived features in the template, used to find the correct indices for mapped derived features
 * @param existingInputFeatures
 * @param existingDerivedFeatures
 * @param newName Optional new name for the feature. If not provided, the original name is retained.
 * @returns An updated TemplateDerivedFeature with recalculated rules based on the new formula and parameter mappings
 */
export function getUpdatedDerivedFeature(
  featureToEdit: TemplateDerivedFeature,
  newFormula: string,
  parameterMappings: FormulaEditorParam[],
  allNewInputs: NewlyAddedInputFeature[],
  allNewDerived: NewlyAddedDerivedFeature[],
  existingInputFeatures: TemplateInput[],
  existingDerivedFeatures: TemplateDerivedFeature[],
  newName?: string,
  outputMetadata?: OutputItemMetadataFields,
): TemplateDerivedFeature {
  const featureName = newName ?? featureToEdit.name;
  const originalInputs = featureToEdit.rules?.[0]?.inputs ?? [];

  return {
    ...featureToEdit,
    ...(newName && { name: newName }),
    ...outputMetadata,
    rules: [
      {
        ...featureToEdit.rules?.[0],
        inputs: createFeatureInputs(
          parameterMappings,
          allNewInputs,
          allNewDerived,
          originalInputs,
          existingInputFeatures,
          existingDerivedFeatures,
        ),
        formulaCreator: toFormulaCreatorRule(
          {
            formula: newFormula,
            parameters: parameterMappings.map((param) => ({
              name: param.identifier,
              item: { id: param.item.id },
            })),
          },
          featureName,
        ),
      },
      ...(featureToEdit.rules?.slice(1) ?? []),
    ],
  };
}

/**
 * Updates an existing derived feature's rule indices to match the current input/derived arrays.
 * This is needed when features are edited multiple times and the arrays change.
 */
export const updateExistingDerivedFeatureIndices = <T extends TemplateDerivedFeature | NewlyAddedDerivedFeature>(
  feature: T,
  currentNewInputFeatures: NewlyAddedInputFeature[],
  currentNewDerivedFeatures: NewlyAddedDerivedFeature[],
  previousNewInputFeatures: NewlyAddedInputFeature[],
  previousNewDerivedFeatures: NewlyAddedDerivedFeature[],
): T => {
  if (!feature.rules?.length) return feature;

  const getFeatureId = (feature: { referenceId?: string; id?: string } | undefined) =>
    feature?.referenceId ?? feature?.id;

  const findNewIndex = <T extends { referenceId?: string; id?: string }>(
    oldIndex: number | undefined,
    previousArray: T[],
    currentArray: T[],
  ): number | undefined => {
    if (oldIndex === undefined) return undefined;

    const previousFeatureId = getFeatureId(previousArray[oldIndex]);
    if (!previousFeatureId) return undefined;

    const newIndex = currentArray.findIndex((f) => getFeatureId(f) === previousFeatureId);
    return newIndex >= 0 ? newIndex : undefined;
  };

  return {
    ...feature,
    rules: feature.rules.map((rule) => ({
      ...rule,
      inputs: rule.inputs?.map((input) => {
        const newInputIndex = findNewIndex(input.inputFeatureIndex, previousNewInputFeatures, currentNewInputFeatures);
        const newDerivedIndex = findNewIndex(
          input.derivedFeatureIndex,
          previousNewDerivedFeatures,
          currentNewDerivedFeatures,
        );

        // Remove old indices and conditionally add new ones

        const { inputFeatureIndex, derivedFeatureIndex, ...baseInput } = input;

        return {
          ...baseInput,
          ...(newInputIndex !== undefined && { inputFeatureIndex: newInputIndex }),
          ...(newDerivedIndex !== undefined && { derivedFeatureIndex: newDerivedIndex }),
        };
      }),
    })),
  };
};

/**
 * Filters parameter mappings to only those actually used in a formula.
 * Whole-token, case-insensitive matching avoids partial identifier matches (e.g., $temp won't
 * match $temperature) while still counting case-divergent references like $Temp for `temp`.
 */
export function filterUsedParameters(parameterMappings: FormulaEditorParam[], formula: string): FormulaEditorParam[] {
  return parameterMappings.filter(
    (param) => !!param.identifier && formulaReferencesVariable(formula, param.identifier),
  );
}

/**
 * Creates input mappings with indices for a derived feature rule.
 * Maps parameter item IDs to input/derived feature indices (for new features)
 * or inputFeatureId (for existing features).
 *
 * For parameters that don't have indices in new arrays, sets inputFeatureId
 * so existing features are tracked as used and not filtered out.
 */
export function createFeatureInputs(
  parameters: FormulaEditorParam[],
  allNewInputs: NewlyAddedInputFeature[],
  allNewDerived: NewlyAddedDerivedFeature[],
  originalInputs: TemplateRuleInputWithFeatureId[] = [],
  existingInputFeatures: TemplateInput[] = [],
  existingDerivedFeatures: TemplateDerivedFeature[] = [],
): TemplateRuleInputWithFeatureId[] {
  const inputIndexByRefId = new Map(allNewInputs.map((f, index) => [f.referenceId, index]));
  const derivedIndexByRefId = new Map(allNewDerived.map((f, index) => [f.referenceId ?? f.id, index]));

  // Tracks which features are already persisted so we can use inputFeatureId instead of creating input or derived
  // feature indices
  const existingInputFeatureIds = new Set([
    ...originalInputs.map((input) => input.inputFeatureId).filter((id): id is string => !!id),
    ...existingInputFeatures.map((f) => f.id),
    ...existingDerivedFeatures.map((f) => f.id),
  ]);

  return parameters.map((param) => {
    const inputIndex = inputIndexByRefId.get(param.item.id) ?? -1;
    const derivedIndex = derivedIndexByRefId.get(param.item.id) ?? -1;
    const templateFeatureId =
      param.inputFeatureId && existingInputFeatureIds.has(param.inputFeatureId) ? param.inputFeatureId : undefined;

    return {
      name: param.item.name,
      type: TemplateRuleInputV1ObjectTypeEnum.FEATUREID,
      featureId: param.item.id,
      ...(inputIndex >= 0 && !templateFeatureId && { inputFeatureIndex: inputIndex }),
      ...(derivedIndex >= 0 && !templateFeatureId && { derivedFeatureIndex: derivedIndex }),
      ...(templateFeatureId && { inputFeatureId: templateFeatureId }),
    };
  });
}

/**
 * Formats a new output feature based on a formula, creating the appropriate formula creator rule and mapping parameter dependencies to input/derived feature indices.
 *
 * @param formula - The formula string
 * @param outputName - The name for the output feature
 * @param usedParams - Parameters used in the formula
 * @param id - The ID for the feature
 * @param newInputs - Input features to reference
 * @param derivedFeatures - Derived features to reference for inputs
 * @param outputMetadata - Optional metadata containing output item type and metadata map
 * @param isOutput - Whether this is an output feature (default: true)
 * @returns A NewlyAddedDerivedFeature with the formula rule
 */
export function formatOutputFeatureFromFormula(
  formula: string,
  outputName: string,
  usedParams: FormulaEditorParam[],
  id: string,
  newInputs: NewlyAddedInputFeature[],
  derivedFeatures: NewlyAddedDerivedFeature[],
  outputMetadata?: OutputItemMetadataFields,
  isOutput = true,
  existingInputFeatures: TemplateInput[] = [],
  existingDerivedFeatures: TemplateDerivedFeature[] = [],
): NewlyAddedDerivedFeature {
  const formulaCreatorRule = toFormulaCreatorRule(
    {
      formula,
      parameters: usedParams.map((param) => ({
        name: param.identifier,
        item: { id: param.item.id },
      })),
    },
    outputName,
  );

  const inputs = createFeatureInputs(
    usedParams,
    newInputs,
    derivedFeatures,
    [],
    existingInputFeatures,
    existingDerivedFeatures,
  );

  return {
    id,
    ...(isOutput ? {} : { referenceId: id }), // Intermediates need referenceId for lookups
    name: outputName,
    isOutput,
    rules: [{ inputs, formulaCreator: formulaCreatorRule }],
    ...outputMetadata,
  };
}

/**
 * Reindexes all new derived feature inputs to match their actual positions in the provided arrays.
 * Used after sorting/filtering features, as these operations invalidate array indices.
 *
 * Enriches rule inputs with featureId first to enable reliable index lookups.
 *
 * @param newDerivedFeatures - Features to reindex
 * @param newInputFeatures - Input features for index lookups
 */
export function reindexNewDerivedFeatures(
  newDerivedFeatures: NewlyAddedDerivedFeature[],
  newInputFeatures: NewlyAddedInputFeature[],
): NewlyAddedDerivedFeature[] {
  // Enrich inputs with featureId so we can track what they reference
  const enrichedFeatures = enrichNewDerivedFeaturesWithFeatureId(newDerivedFeatures, newInputFeatures);

  const inputIndexByRefId = new Map(newInputFeatures.map((f, index) => [f.referenceId, index]));
  const derivedIndexById = new Map<string, number>();
  enrichedFeatures.forEach((f, index) => {
    const id = f.referenceId ?? f.id;
    if (id) derivedIndexById.set(id, index);
  });

  return enrichedFeatures.map((feature) => {
    if (!feature.rules?.length) return feature;

    return {
      ...feature,
      rules: feature.rules.map((rule) => ({
        ...rule,
        inputs: rule.inputs?.map((input) => {
          // If this input references an existing persisted feature, don't reindex
          if (input.inputFeatureId) {
            return input;
          }

          const featureId = getFeatureIdFromInput(input);
          if (!featureId) {
            console.warn('Input missing featureId, cannot reindex reliably:', input);
            return input;
          }

          const newInputIndex = inputIndexByRefId.get(featureId);
          const newDerivedIndex = derivedIndexById.get(featureId);

          const { inputFeatureIndex, derivedFeatureIndex, ...baseInput } = input;

          return {
            ...baseInput,
            ...(newInputIndex !== undefined && { inputFeatureIndex: newInputIndex }),
            ...(newDerivedIndex !== undefined && { derivedFeatureIndex: newDerivedIndex }),
          };
        }),
      })),
    };
  });
}

/**
 * Generates a default name for a new output feature.
 * Uses the same naming pattern as "New Column" in Asset Groups.
 * If editing an existing feature, returns the existing name.
 */
export function getDefaultOutputName(
  isCreatingNew: boolean,
  featureToEdit: TemplateDerivedFeature | NewlyAddedDerivedFeature | undefined,
  existingOutputs: NewlyAddedDerivedFeature[],
): string {
  if (!isCreatingNew && featureToEdit) {
    return featureToEdit.name;
  }
  const usedNewOutputNames = existingOutputs.filter((f) => f.isOutput).map((f) => f.name);
  return getNextDefaultName(usedNewOutputNames, 'TEMPLATES.NEW_OUTPUT');
}

function isNewlyAddedDerivedFeature(
  feature: TemplateDerivedFeature | NewlyAddedDerivedFeature,
): feature is NewlyAddedDerivedFeature {
  return 'referenceId' in feature;
}

/** Converts an intermediate to a new input feature representation. */
export function convertIntermediateToInputFeature(
  intermediate: TemplateDerivedFeature | NewlyAddedDerivedFeature,
): NewlyAddedInputFeature {
  const featureId = isNewlyAddedDerivedFeature(intermediate)
    ? (intermediate.referenceId ?? intermediate.id)
    : intermediate.id;
  const outputItemType = intermediate.outputItemType
    ? captureInputRestrictionType(intermediate.outputItemType)
    : undefined;
  return {
    id: featureId!,
    referenceId: featureId!,
    name: intermediate.name,
    originalName: intermediate.name,
    description: '',
    isCollapsed: false,
    ...(outputItemType !== undefined && { outputItemType }),
  };
}

/** Marks an intermediate as toggled to input and handles existing vs new feature differences. */
export function markIntermediateAsInput(
  intermediateId: string,
  newIntermediate: NewlyAddedDerivedFeature | undefined,
  existingIntermediate: TemplateDerivedFeature | undefined,
  newDerivedFeatures: NewlyAddedDerivedFeature[],
  existingDerivedFeatures: TemplateDerivedFeature[],
): { updatedNewDerivedFeatures: NewlyAddedDerivedFeature[]; updatedExistingDerivedFeatures: TemplateDerivedFeature[] } {
  if (newIntermediate) {
    return {
      updatedNewDerivedFeatures: newDerivedFeatures.map((f) =>
        getDerivedFeatureId(f) === intermediateId ? { ...f, intermediateAsInput: true } : f,
      ),
      updatedExistingDerivedFeatures: existingDerivedFeatures,
    };
  }

  if (!existingIntermediate) {
    throw new Error(
      `markIntermediateAsInput: neither newIntermediate nor existingIntermediate provided for ${intermediateId}`,
    );
  }

  // Move existing intermediate to newDerivedFeatures, preserving full metadata for restore
  const convertedIntermediate: NewlyAddedDerivedFeature = {
    id: existingIntermediate.id,
    referenceId: existingIntermediate.id,
    name: existingIntermediate.name,
    rules: existingIntermediate.rules,
    isOutput: false,
    intermediateAsInput: true,
    wasExistingIntermediate: true,
    originalFeature: existingIntermediate,
  };

  return {
    updatedNewDerivedFeatures: [...newDerivedFeatures, convertedIntermediate],
    updatedExistingDerivedFeatures: existingDerivedFeatures.filter((f) => f.id !== intermediateId),
  };
}

/** Collects IDs of all intermediates that have been toggled to inputs. */
export function getToggledIntermediateIds(newDerivedFeatures: NewlyAddedDerivedFeature[]): Set<string> {
  return new Set(
    newDerivedFeatures
      .filter((f) => f.intermediateAsInput)
      .map((f) => f.referenceId ?? f.id)
      .filter((id): id is string => !!id),
  );
}

/**
 * Guarantees a restored intermediate lands in the derived list, not the input list.
 */
export function ensureIntermediateRestored(
  restoredIntermediate: NewlyAddedDerivedFeature,
  newInputs: NewlyAddedInputFeature[],
  newDerived: NewlyAddedDerivedFeature[],
): { newInputs: NewlyAddedInputFeature[]; newDerived: NewlyAddedDerivedFeature[] } {
  const restoredId = getDerivedFeatureId(restoredIntermediate);
  const correctedInputs = newInputs.filter((f) => (f.referenceId ?? f.id) !== restoredId);
  const alreadyDerived = newDerived.some((f) => getDerivedFeatureId(f) === restoredId);
  const correctedDerived = alreadyDerived
    ? newDerived
    : [...newDerived, { ...restoredIntermediate, intermediateAsInput: undefined }];
  return { newInputs: correctedInputs, newDerived: correctedDerived };
}

/** Creates a Map from feature id/referenceId to array index for O(1) lookups. */
function buildInputIndexMap(features: (NewlyAddedInputFeature | TemplateInput)[]): Map<string, number> {
  const map = new Map<string, number>();
  features.forEach((f, index) => {
    if (f.referenceId) map.set(f.referenceId, index);
    if (f.id) map.set(f.id, index);
  });
  return map;
}

/** Updates existing derived features to reference toggled intermediates using inputFeatureIndex. */
export function updateExistingDerivedRefsToToggledIntermediates(
  existingDerivedFeatures: TemplateDerivedFeature[],
  toggledIntermediateIds: Set<string>,
  allInputFeatures: (NewlyAddedInputFeature | TemplateInput)[],
): TemplateDerivedFeature[] {
  const inputIndexById = buildInputIndexMap(allInputFeatures);

  return existingDerivedFeatures.map((feature) => ({
    ...feature,
    rules: feature.rules?.map((rule) => ({
      ...rule,
      inputs: rule.inputs?.map((input) => {
        if (typeof input !== 'object') return input;

        const lookupId = input.inputFeatureId || (hasFeatureId(input) ? input.featureId : undefined);

        if (lookupId && toggledIntermediateIds.has(lookupId)) {
          const inputIndex = inputIndexById.get(lookupId);
          if (inputIndex === undefined) {
            return input;
          }

          const { inputFeatureId, derivedFeatureIndex, ...baseInput } = input;
          return { ...baseInput, featureId: lookupId, inputFeatureIndex: inputIndex };
        }
        return input;
      }),
    })),
  }));
}

/**
 * Updates existing derived features when an existing intermediate is restored from input.
 * Converts inputFeatureIndex references to the restored intermediate back to inputFeatureId,
 * and reindexes remaining inputFeatureIndex references.
 */
export function updateExistingDerivedRefsAfterRestore(
  existingDerivedFeatures: TemplateDerivedFeature[],
  restoredIntermediateId: string,
  allInputFeatures: (NewlyAddedInputFeature | TemplateInput)[],
): TemplateDerivedFeature[] {
  const inputIndexById = buildInputIndexMap(allInputFeatures);

  return existingDerivedFeatures.map((feature) => ({
    ...feature,
    rules: feature.rules?.map((rule) => ({
      ...rule,
      inputs: rule.inputs?.map((input) => {
        if (typeof input !== 'object') return input;

        const lookupId = input.inputFeatureId || (hasFeatureId(input) ? input.featureId : undefined);

        // If this references the restored intermediate, convert to inputFeatureId
        if (lookupId === restoredIntermediateId) {
          const { inputFeatureIndex, derivedFeatureIndex, ...baseInput } = input;
          return { ...baseInput, inputFeatureId: restoredIntermediateId };
        }

        // If the input has an inputFeatureIndex, reindex it to account for the restored intermediate being removed from inputs
        if (input.inputFeatureIndex !== undefined) {
          const newIndex = lookupId ? inputIndexById.get(lookupId) : undefined;

          if (newIndex === undefined) {
            return input;
          }

          return { ...input, inputFeatureIndex: newIndex };
        }
        return input;
      }),
    })),
  }));
}

export const exposedForTesting = {
  isFeatureUsedByOtherOutputs,
  convertNewRules,
  getUpdatedInputsAndOutputs,
  convertDerivedFeature,
  updateInputs,
  updateEditedDerivedFeature,
  getUpdatedIntermediates,
  editTemplate,
  handleSaveDraftInToolPane,
};
